import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';

import { Form, PrimaryButton, FieldTextInput, FieldCheckbox, CustomExtendedDataField } from '../../../components';

import FieldSelectUserType from '../FieldSelectUserType';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';

import css from './ConfirmSignupForm.module.css';
import { USER_TYPE } from '../../../util/enums';

const SELLER_AGREEMENT_URL = 'https://drive.google.com/file/d/1SrAGR7jRPGKzlpAIOGncDwKB55huWo7d/view?pli=1';
const BUYER_AGREEMENT_URL = 'https://drive.google.com/file/d/1Grmz0x3AOdBN5QR2SGbUDM5Pe2SnSzAY/view?usp=sharing';

const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

const ConfirmSignupFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    initialValues={{
      userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes) || USER_TYPE.PROVIDER,
    }}
    render={formRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        authInfo,
        idp,
        preselectedUserType,
        userTypes,
        userFields,
        values,
      } = formRenderProps;

      const { userType } = values || {};

      // email
      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'ConfirmSignupForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'ConfirmSignupForm.emailInvalid',
        })
      );

      // Custom user fields. Since user types are not supported here,
      // only fields with no user type id limitation are selected.
      const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

      const noUserTypes = !userType && !(userTypes?.length > 0);
      const userTypeConfig = userTypes.find(config => config.userType === userType);
      const showDefaultUserFields = userType || noUserTypes;
      const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

      const userAgreementRequired = value => {
        const isAccepted = Array.isArray(value) ? value.includes('accepted') : (value === true || value === 'accepted');
        return isAccepted ? undefined : intl.formatMessage({ id: 'SignupForm.userAgreementRequired' });
      };

      const showUserAgreement = showDefaultUserFields && userType === USER_TYPE.PROVIDER;

      const userAgreementLabel = showUserAgreement ? (
        <>
          I acknowledge that by creating this account, I am entering into a legally binding agreement with Balloon Artist Marketplace and agree to be bound by the{' '}
          <a href={SELLER_AGREEMENT_URL} target="_blank" rel="noopener noreferrer" className={css.userAgreementLink}>
            Seller Agreement
          </a>
          {' '}and{' '}
          <a href={SELLER_AGREEMENT_URL} target="_blank" rel="noopener noreferrer" className={css.userAgreementLink}>
            Legal Terms
          </a>
          . I understand that all digital purchases are final and that my use of purchased files is governed by the selected license and the{' '}
          <a href={BUYER_AGREEMENT_URL} target="_blank" rel="noopener noreferrer" className={css.userAgreementLink}>
            Buyer Agreement
          </a>
          {' '}and{' '}
          <a href={BUYER_AGREEMENT_URL} target="_blank" rel="noopener noreferrer" className={css.userAgreementLink}>
            Legal Terms
          </a>
          .
        </>
      ) : null;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      // If authInfo is not available we should not show the ConfirmForm
      if (!authInfo) {
        return;
      }

      // Initial values from idp provider
      const { email, firstName, lastName } = authInfo;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <FieldSelectUserType
            name="userType"
            userTypes={userTypes}
            hasExistingUserType={!!preselectedUserType}
            intl={intl}
          />

          {showDefaultUserFields ? (
            <div className={css.defaultUserFields}>
              <FieldTextInput
                type="email"
                id={formId ? `${formId}.email` : 'email'}
                name="email"
                autoComplete="email"
                label={intl.formatMessage({
                  id: 'ConfirmSignupForm.emailLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'ConfirmSignupForm.emailPlaceholder',
                })}
                initialValue={email}
                validate={validators.composeValidators(emailRequired, emailValid)}
              />
              <div className={css.name}>
                <FieldTextInput
                  className={css.firstNameRoot}
                  type="text"
                  id={formId ? `${formId}.firstName` : 'firstName'}
                  name="firstName"
                  autoComplete="given-name"
                  label={intl.formatMessage({
                    id: 'ConfirmSignupForm.firstNameLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'ConfirmSignupForm.firstNamePlaceholder',
                  })}
                  initialValue={firstName}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'ConfirmSignupForm.firstNameRequired',
                    })
                  )}
                />
                <FieldTextInput
                  className={css.lastNameRoot}
                  type="text"
                  id={formId ? `${formId}.lastName` : 'lastName'}
                  name="lastName"
                  autoComplete="family-name"
                  label={intl.formatMessage({
                    id: 'ConfirmSignupForm.lastNameLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'ConfirmSignupForm.lastNamePlaceholder',
                  })}
                  initialValue={lastName}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'ConfirmSignupForm.lastNameRequired',
                    })
                  )}
                />
              </div>

              <UserFieldDisplayName
                formName="ConfirmSignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              />
              <UserFieldPhoneNumber
                formName="ConfirmSignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              />

              <FieldTextInput
                className={css.row}
                type="text"
                id={formId ? `${formId}.instagram` : 'instagram'}
                name="instagram"
                label={intl.formatMessage({ id: 'SignupForm.instagramLabel' })}
                placeholder="https://instagram.com/yourprofile"
                validate={validators.urlRequired(
                  intl.formatMessage({ id: 'SignupForm.validInstagramUrl' })
                )}
              />

              <FieldTextInput
                className={css.row}
                type="text"
                name="website"
                id={formId ? `${formId}.website` : 'website'}
                label={intl.formatMessage({ id: 'SignupForm.websiteLabel' })}
                placeholder="https://www.yourwebsite.com"
                validate={validators.urlRequired(
                  intl.formatMessage({ id: 'SignupForm.validWebsiteUrl' })
                )}
              />
            </div>
          ) : null}

          {showCustomUserFields ? (
            <div className={css.customFields}>
              {userFieldProps.map(({ key, ...fieldProps }) => (
                <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
              ))}
            </div>
          ) : null}

          {showUserAgreement ? (
            <div className={css.userAgreement}>
              <FieldCheckbox
                id={formId ? `${formId}.userAgreementAccepted` : 'userAgreementAccepted'}
                name="userAgreementAccepted"
                value="accepted"
                label={userAgreementLabel}
                validate={userAgreementRequired}
              />
            </div>
          ) : null}

          <div className={css.bottomWrapper}>
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="ConfirmSignupForm.signUp" values={{ idp: idp }} />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

/**
 * A component that renders the confirm signup form, which is used with SSO authentication.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @param {string} props.preselectedUserType - The preselected user type
 * @param {propTypes.userTypes} props.userTypes - The user types
 * @param {propTypes.listingFields} props.userFields - The user fields
 * @returns {JSX.Element}
 */
const ConfirmSignupForm = props => {
  const intl = useIntl();
  return <ConfirmSignupFormComponent {...props} intl={intl} />;
};

export default ConfirmSignupForm;
